import { mobileXLargeMax, mobileXLargeMin, tabletMax } from '@propertypal/shared/src/constants/mediaQueries';
import styled, { css } from 'styled-components';
import { Container as Button } from '../buttons/Button.style';
import { Heading } from '../typography';

const FEATURE_X_SPACE = 70;
const FEATURE_X_PADDING = 40;
const FEATURE_WIDTH = 270;
const FEATURE_POSITION = -(FEATURE_WIDTH - FEATURE_X_SPACE);
const FEATURE_BREAKPOINT = 1170;
const FEATURE_MAX_BREAKPOINT = `(max-width: ${FEATURE_BREAKPOINT}px)`;
const FEATURE_MIN_BREAKPOINT = `(min-width: ${FEATURE_BREAKPOINT + 1}px)`;
const FEATURE_MOB_GAP = 20;
const BUTTON_MAX_W = 290;

export const Container = styled.div<{ pb?: number }>`
  background-color: rgba(216, 216, 216, 0.2);
  padding: 60px 0 ${({ pb }) => pb || 125}px 0;

  @media (${tabletMax}) {
    padding-bottom: 60px;
  }
`;

export const Wrapper = styled.div`
  padding: 0 10px;
  max-width: 1064px;
  margin: 0 auto;

  @media (${mobileXLargeMax}) {
    ${Heading} {
      font-size: 34px;
    }
  }
`;

export const Content = styled.div`
  display: flex;
  margin-top: 35px;
`;

export const MockupContainer = styled.div<{ shiftLeft?: boolean }>`
  position: relative;
  width: 100%;
  margin: 0 auto;
  max-width: 700px;

  @media (${FEATURE_MIN_BREAKPOINT}) {
    ${({ shiftLeft }) =>
      shiftLeft
        ? css`
            margin-left: 100px;
          `
        : ''}
  }

  @media (${tabletMax}) {
    max-width: 90vw;
    flex-basis: 700px;
  }
`;

export const MockupWrapper = styled.div<{ aspectRatio: string }>`
  width: 100%;
  aspect-ratio: ${({ aspectRatio }) => aspectRatio};
  position: relative;
`;

export const MockupContent = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const MockupImage = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const PromoteBadge = styled.div<{
  hasSubtext?: boolean;
  textMw?: number;
  titleMt?: number;
  titleMw?: number;
  size?: number;
  mobSize?: number;
  mobTextMw?: number;
}>`
  position: absolute;
  transform: rotate(-12deg);
  width: ${({ hasSubtext, size }) => size || (hasSubtext ? 180 : 170)}px;
  height: ${({ hasSubtext, size }) => size || (hasSubtext ? 180 : 170)}px;
  top: -25px;
  z-index: 3;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.2);
  background-color: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.white};
  border-radius: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 5px;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.3);

  .badge-title {
    font-size: 30px;
    font-weight: bold;
    line-height: 1.13;
    max-width: ${({ titleMw }) => titleMw || 170}px;
    margin-top: ${({ titleMt }) => titleMt || 0}px;
  }

  .badge-text {
    font-size: 16px;
    line-height: 1.25;
    max-width: ${({ textMw }) => textMw || 120}px;
  }

  .badge-subtext {
    font-size: 12px;
    line-height: 1.17;
    max-width: 120px;
    margin-top: 5px;
  }

  @media (${tabletMax}) {
    width: ${({ mobSize }) => mobSize || 130}px;
    height: ${({ mobSize }) => mobSize || 130}px;

    .badge-title {
      font-size: 22px;
      max-width: 120px;
    }

    .badge-text {
      font-size: 12px;
      max-width: ${({ mobTextMw }) => mobTextMw || 100}px;
    }

    .badge-subtext {
      font-size: 10px;
      max-width: 120px;
      margin-top: 0px;
    }
  }
`;

export const ButtonContainer = styled.div`
  margin-top: 25px;
  width: 100%;
  display: flex;
  justify-content: center;

  a.link-button {
    @media (${mobileXLargeMin}) {
      max-width: ${BUTTON_MAX_W}px;
    }
    width: 100%;
  }

  ${Button} {
    height: 54px;

    p {
      font-weight: 800;
    }
  }
`;

export const PromoteFeatures = styled.div`
  @media (${FEATURE_MAX_BREAKPOINT}) {
    display: flex;
    flex-direction: column;
    gap: ${FEATURE_MOB_GAP}px;
    margin-top: 40px;
    align-items: center;
  }
`;

const Features = css`
  display: flex;
  gap: ${FEATURE_MOB_GAP}px;
  flex-direction: column;

  @media (${FEATURE_MAX_BREAKPOINT}) {
    width: 100%;
  }

  @media (${FEATURE_MIN_BREAKPOINT}) {
    position: absolute;
    bottom: 60px;
    gap: 65px;
    width: ${FEATURE_WIDTH}px;
  }
`;

export const FeatureItem = styled.div`
  border-radius: 8px;
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.1);
  background-color: ${({ theme }) => theme.primaryTranslucent};
  width: 100%;
  padding: 14px 20px;
`;

export const LeftFeatures = styled.div`
  ${Features};

  @media (${FEATURE_MIN_BREAKPOINT}) {
    left: ${FEATURE_POSITION}px;

    ${FeatureItem} {
      padding-right: ${FEATURE_X_PADDING}px;
    }
  }
`;

export const RightFeatures = styled.div`
  ${Features};

  @media (${FEATURE_MIN_BREAKPOINT}) {
    right: ${FEATURE_POSITION}px;

    ${FeatureItem} {
      padding-left: ${FEATURE_X_PADDING}px;
    }
  }
`;
